/**
 * Quản lý Three.js: renderer, scene, camera, đèn, shadow, OrbitControls.
 * Render **on-demand** (ADR-012): chỉ vẽ khi có thay đổi (camera/animation/selection),
 * không chạy vòng lặp 60fps liên tục → tiết kiệm pin mobile.
 */
import {
  AmbientLight,
  Box3,
  BufferGeometry,
  Color,
  DirectionalLight,
  EdgesGeometry,
  Float32BufferAttribute,
  Group,
  HemisphereLight,
  LineBasicMaterial,
  LineSegments,
  type Material,
  Mesh,
  MeshBasicMaterial,
  MeshNormalMaterial,
  MeshPhongMaterial,
  Object3D,
  OrthographicCamera,
  PerspectiveCamera,
  PlaneGeometry,
  Raycaster,
  Scene,
  ShadowMaterial,
  Sphere,
  Texture,
  Vector2,
  Vector3,
  WebGLRenderer,
  WebGLRenderTarget,
  HalfFloatType,
} from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { EffectComposer } from 'three/addons/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/addons/postprocessing/RenderPass.js';
import { GTAOPass } from 'three/addons/postprocessing/GTAOPass.js';
import { OutputPass } from 'three/addons/postprocessing/OutputPass.js';
import { isMeshInline, isMeshRef } from '../lib/types/wvm';
import type { BandMaterial, Groove, LightingPreset, WvmDocument, WvmLighting, WvmPart } from '../lib/types/wvm';
import type { MaterialTexture } from '../lib/api/client';
import {
  buildAssetEdges,
  buildAssetGeometry,
  buildBandGeo,
  buildColorMap,
  buildGrooveGeo,
  buildGrooveRimGeo,
  buildMeshRefMesh,
  buildPartMesh,
  DEFAULT_BAND_COLOR,
  expandMeshRefToInline,
  makeBandMaterial,
  makeGrooveMaterial,
  makeGrooveRimMaterial,
  meshRefPivotMatrix,
  partCenter,
  resolveColor,
} from './MeshBuilder';
import { TextureManager } from './TextureManager';
import { explodeOffset } from './explode';

/**
 * Chế độ hiển thị:
 * - material            : vật liệu (texture) đặc.
 * - matedge             : vật liệu + nét nhìn thấy đậm (không nét khuất).
 * - projection          : nền sáng phẳng + nét.
 * - xray                : bề mặt trong mờ + nét thấy đậm/khuất nhạt.
 * - wireframe           : bề mặt trong suốt, MỌI nét (thấy + khuất) đều đậm, không vật liệu.
 * - wireframe-hidden    : bề mặt trong suốt, nét thấy đậm + nét khuất nhạt.
 */
export type RenderMode = 'material' | 'matedge' | 'projection' | 'xray' | 'wireframe' | 'wireframe-hidden';

/**
 * Preset ánh sáng (tác giả chọn, áp cho mọi người xem — xem WvmLighting/03h):
 * - `even`   — chiếu đều mọi phía (hemisphere + rim) → mặt quay xa key không tối, đọc vân rõ.
 * - `studio` — 3-point tương phản hơn, khối nổi/có chiều sâu, bóng mềm.
 * - `bright` — tăng cường độ tổng (ambient/hemisphere) cho sáng hơn toàn cục.
 */
export type { LightingPreset };

/** Tham số bề mặt 04e (chỉ part đã map — xem 04e §1/§3). Áp trong applyAppearance ở mode 'material'. */
export interface SurfaceParams {
  /** 0..100 → opacity = 1 - t/100. */
  transparency: number;
  /** 0..100 → color.setScalar(1 + 0.5*b/100), cap 1.5×. */
  brightness: number;
  /** 0..100 → shininess 30..120, specular 0x222222..0x888888. */
  reflectivity: number;
}

/** Một part đã dựng: mesh (căn gốc) nằm trong pivot (đặt tại tâm part). */
export interface PartObject {
  part: WvmPart;
  mesh: Mesh;
  pivot: Group;
  /**
   * Group BAO NGOÀI pivot, chỉ mang offset "tách rời" (sơ đồ lắp đặt) → modelRoot→explodeGroup→pivot→mesh.
   * Tách tầng khỏi MotionEngine (sở hữu pivot mỗi frame) nên explode cộng hưởng sạch với chuyển động.
   */
  explodeGroup: Group;
  /** Tâm chi tiết (scene units) lúc lắp ráp — gốc để tính vector tách rời (xem explode.ts). */
  explodeCenter: Vector3;
  /** đường cạnh NHÌN THẤY (depthTest → bị tấm trước che). null với cylinder. */
  edge: LineSegments | null;
  /** đường cạnh KHUẤT (không depthTest → luôn vẽ, mờ; chỉ bật ở wireframe/x-ray). */
  edgeHidden: LineSegments | null;
  /** 04d: viền miệng rãnh xoi (chỉ part ĐÃ khoét lõm); null nếu không. Styling đồng bộ nét thấy. */
  grooveRim: LineSegments | null;
  /** màu vật liệu gốc (để khôi phục khi đổi render mode / bỏ chọn). */
  baseColor: Color;
  /** texture đã resolve (04d); null = màu trơn. Chỉ bật map ở mode 'material'. */
  tex: Texture | null;
  /** tham số bề mặt 04e; null = không settings → render như 04d. Áp lại mỗi applyAppearance. */
  surface: SurfaceParams | null;
}

const SELECT_COLOR = 0x00c6ff; // token partHighlight

/** Hướng đèn KEY (đơn vị) — vị trí thật đặt theo bán kính model, xem updateShadowFrustum. */
const KEY_DIR = new Vector3(100, 160, 80).normalize();

/** Hàm animator: trả true nếu còn cần vẽ tiếp (animation chưa tới đích). */
export type Animator = () => boolean;

const ISO_DIR = new Vector3(1, 0.65, 1).normalize();
const WORLD_UP = new Vector3(0, 1, 0);

export class SceneManager {
  readonly scene = new Scene();
  readonly camera: PerspectiveCamera;
  readonly orthoCamera = new OrthographicCamera(-1, 1, 1, -1, 0.1, 10000);
  readonly renderer: WebGLRenderer;
  readonly controls: OrbitControls;

  /** SSAO (GTAO) — làm tối recess/rãnh xoi tự động. Chỉ áp cho camera PERSPECTIVE (view chính);
   *  ortho (hình chiếu kỹ thuật) render thẳng. `null` nếu WebGL không dựng được composer (fallback an toàn). */
  private composer: EffectComposer | null = null;
  private ssaoEnabled = true;

  /** camera đang render: perspective (mặc định/iso) hoặc ortho (hình chiếu). */
  private active!: PerspectiveCamera | OrthographicCamera;
  private orthoDir: 'front' | 'side' | 'top' | null = null;
  /** bán kính khung bao TOÀN model (scene units) — cho near/far co/giãn khi zoom. */
  private modelRadius = 1;

  private readonly modelRoot = new Group();
  private readonly shadowPlane: Mesh;
  /** đèn KEY của preset hiện tại (đèn duy nhất đổ bóng) — cần để chỉnh shadow camera theo model. */
  private keyLight: DirectionalLight | null = null;
  /** target của KEY: giữ CỐ ĐỊNH trong scene (clearLights chỉ dọn lightGroup) → không rò qua các preset. */
  private readonly keyTarget = new Object3D();
  /** chứa toàn bộ đèn của preset hiện tại — đổi preset = clear + dựng lại. */
  private readonly lightGroup = new Group();
  private lighting: LightingPreset = 'even';
  /** master on/off + cường độ 0..100 (50 = đúng độ sáng gốc của preset). */
  private lightsOn = true;
  private lightLevel = 50;
  private partObjects: PartObject[] = [];
  private readonly textures: TextureManager;

  /** Sơ đồ tách rời: tâm toả (scene units) + mức 0..1 + phạm vi (null = toàn model, else tập id cụm chọn). */
  private readonly explodeCenterV = new Vector3();
  private readonly _exMin = new Vector3();
  private readonly _exMax = new Vector3();
  private explodeAmt = 0;
  private explodeIds: Set<number> | null = null;

  private selectedId: number | null = null;
  // Tập chi tiết được tô NHƯ ĐANG CHỌN (teal) — dùng cho "chọn cụm" (sơ đồ tách rời), đồng màu click chuột.
  private selectedSet: Set<number> | null = null;
  // Chi tiết đang hover (từ nhãn sơ đồ GĐ2) → tô nổi hơn cả cụm teal; null = không hover.
  private hoverId: number | null = null;
  private renderMode: RenderMode = 'matedge'; // mặc định "Vật liệu + Nét" (khớp default Viewer)
  private searchHits: Set<number> | null = null; // chi tiết khớp tìm kiếm (null = không tìm)
  private groupHl: Set<number> | null = null; // chi tiết cùng nhóm chuyển động với part đang chọn (author)
  private guideNormalMat?: MeshNormalMaterial; // 04a: fill normal-pass (lazy, dùng lại)
  private guideBlackMat?: MeshBasicMaterial; // 04a: fill đen ghi depth cho line-pass (lazy)
  // 04a nẹp: material dùng CHUNG theo materialId (recolor 1 lần) + danh sách để map name↔id.
  private bandMaterialsById = new Map<string, MeshPhongMaterial>();
  private bandMaterials: BandMaterial[] = [];

  private readonly raycaster = new Raycaster();
  private readonly pointer = new Vector2();

  private rafId = 0;
  private readonly animators = new Set<Animator>();
  private readonly frameCallbacks = new Set<() => void>();
  private resizeObserver?: ResizeObserver;
  private disposed = false;

  /**
   * `mobileSafe`: bậc render nhẹ cho di động (cờ per-account của chủ model, xem WvmDocument.mobileSafeRender).
   * BẬT → KHÔNG dựng composer/GTAO, pixelRatio ≤1.5, shadow 1024 — giảm bộ nhớ GPU để tránh crash WebGL
   * (iOS/WebKit) ở model nặng. Handler mất-context LUÔN gắn (không phụ thuộc cờ) như lưới an toàn.
   */
  private mobileSafe: boolean;

  /**
   * 04d: rãnh xoi vẽ LÕM THẬT (CSG khoét) hay decal phẳng. Chính sách thiết bị: desktop = lõm
   * (GTAO screen-space nổi khe cả ở chế độ phẳng mặc định); mobile-safe = decal (CSG đắt lúc load +
   * GTAO vắng → lợi thấp). User đổi qua setGroovesRecess; đồng bộ khi setMobileSafe đổi bậc.
   */
  private recessGrooves: boolean;

  constructor(
    private readonly canvas: HTMLCanvasElement,
    opts: { mobileSafe?: boolean } = {},
  ) {
    this.mobileSafe = opts.mobileSafe ?? false;
    this.recessGrooves = !this.mobileSafe;
    this.renderer = new WebGLRenderer({
      canvas,
      antialias: true,
      alpha: true,
      // dev: giữ buffer để screenshot/verify canvas idle (on-demand). Prod tắt cho nhẹ.
      preserveDrawingBuffer: import.meta.env.DEV,
    });
    // mobile-safe: pixelRatio trần 1.5 (framebuffer ~2.25× thay vì 4× so với 1×) — cứu RAM nhiều nhất.
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, this.mobileSafe ? 1.5 : 2));
    // Mất WebGL context (iOS thu hồi khi thiếu RAM) → preventDefault để trình duyệt PHỤC HỒI được;
    // khi khôi phục, Three.js tự nạp lại tài nguyên GL → chỉ cần vẽ lại. Không có = màn trắng vĩnh viễn.
    canvas.addEventListener('webglcontextlost', this.onContextLost);
    canvas.addEventListener('webglcontextrestored', this.onContextRestored);
    this.renderer.setClearColor(0x000000, 0); // trong suốt → nền CSS gradient hiện qua
    this.renderer.shadowMap.enabled = true;
    this.textures = new TextureManager(this.renderer);

    this.camera = new PerspectiveCamera(42, 1, 0.3, 8000);
    this.camera.position.set(60, 45, 60);
    this.active = this.camera;

    // đèn — preset chọn được (even/studio/bright). Tĩnh, không ảnh hưởng render on-demand.
    this.scene.add(this.lightGroup);
    this.applyLighting(this.lighting);

    // mặt nhận bóng
    this.shadowPlane = new Mesh(new PlaneGeometry(2000, 2000), new ShadowMaterial({ opacity: 0.12 }));
    this.shadowPlane.rotation.x = -Math.PI / 2;
    this.shadowPlane.receiveShadow = true;
    this.scene.add(this.shadowPlane);
    this.scene.add(this.keyTarget);

    this.scene.add(this.modelRoot);

    this.controls = new OrbitControls(this.camera, canvas);
    this.controls.zoomToCursor = true; // zoom về vị trí con trỏ thay vì tâm sản phẩm
    this.controls.enableDamping = false; // giữ render on-demand thuần
    this.controls.addEventListener('change', this.onControlsChange);

    this.resizeObserver = new ResizeObserver(() => this.handleResize());
    this.resizeObserver.observe(canvas);
    this.handleResize();

    // mobile-safe: BỎ composer/GTAO — không cấp phát render target MSAA + các target nội bộ của GTAO.
    if (!this.mobileSafe) this.setupComposer();
  }

  /**
   * Đổi bậc render mobile-safe lúc chạy. Dùng khi CHỦ model TẮT tùy chọn (meta.mobileSafeRender=false)
   * → khôi phục chất lượng đầy đủ trên mobile. Mặc định viewer đã áp mobile-safe theo thiết bị lúc dựng,
   * nên đây chỉ là đường nâng-cấp hiếm (opt-out). Không đổi gì nếu trùng trạng thái.
   */
  setMobileSafe(on: boolean): void {
    if (on === this.mobileSafe) return;
    this.mobileSafe = on;
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, on ? 1.5 : 2));
    if (on) {
      this.composer?.dispose(); // safe: bỏ composer/GTAO → giải phóng render target
      this.composer = null;
    } else if (!this.composer) {
      this.setupComposer(); // full: dựng lại composer/GTAO
    }
    // dựng lại đèn theo shadow map size mới NHƯNG tôn trọng trạng thái bật/tắt hiện tại:
    // nếu đang TẮT chiếu sáng (phẳng) thì giữ phẳng, không bật lại đèn KEY/đổ bóng.
    if (this.lightsOn) this.applyLighting(this.lighting);
    else this.applyFlat();
    this.handleResize(); // đồng bộ renderer + composer theo pixelRatio mới
    // 04d: đồng bộ chính sách rãnh theo bậc (mobile-safe → decal; full → lõm thật). Dựng lại part grooved.
    this.setGroovesRecess(!on);
    this.invalidate();
  }

  /** Mất context: chặn hành vi mặc định (cho phép phục hồi) + dừng vòng vẽ hiện tại. */
  private onContextLost = (e: Event): void => {
    e.preventDefault();
    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
      this.rafId = 0;
    }
    console.warn('[SceneManager] WebGL context lost → chờ phục hồi');
  };

  /** Context phục hồi: Three.js đã tự nạp lại tài nguyên GL → chỉ cần vẽ lại. */
  private onContextRestored = (): void => {
    console.warn('[SceneManager] WebGL context restored → vẽ lại');
    if (!this.disposed) this.invalidate();
  };

  /**
   * Dựng EffectComposer + GTAO (SSAO) cho camera perspective. Render target MSAA (samples:4) để GIỮ
   * khử răng cưa (composer không kế thừa antialias của canvas). GTAO blend nhân alpha (src_a×dst_a) nên
   * nền alpha=0 GIỮ trong suốt (gradient CSS vẫn hiện). Lỗi khởi tạo → composer=null → tick() render thẳng.
   */
  private setupComposer(): void {
    try {
      const size = this.renderer.getDrawingBufferSize(new Vector2());
      const rt = new WebGLRenderTarget(Math.max(1, size.x), Math.max(1, size.y), {
        type: HalfFloatType,
        samples: 4, // MSAA
      });
      const composer = new EffectComposer(this.renderer, rt);
      composer.setPixelRatio(this.renderer.getPixelRatio());
      composer.addPass(new RenderPass(this.scene, this.camera));
      const gtao = new GTAOPass(this.scene, this.camera, size.x, size.y);
      gtao.output = GTAOPass.OUTPUT.Default;
      // Chỉnh cho scene mm×SCALE(0.065): rãnh sâu ~5mm ≈ 0.3 đơn vị; radius rộng hơn để tối cả rãnh lẫn góc.
      gtao.updateGtaoMaterial({ radius: 1.2, distanceExponent: 1, thickness: 1, scale: 1.2, samples: 16 });
      gtao.updatePdMaterial({ lumaPhi: 10, depthPhi: 2, normalPhi: 3, radius: 4, radiusExponent: 1 });
      composer.addPass(gtao);
      composer.addPass(new OutputPass());
      const w = this.canvas.clientWidth || 1, h = this.canvas.clientHeight || 1;
      composer.setSize(w, h);
      this.composer = composer;
    } catch (e) {
      console.warn('[SceneManager] SSAO/composer init lỗi → render thẳng (không AO):', e);
      this.composer = null;
    }
  }

  /** Bật/tắt SSAO (recess/rãnh). */
  setSsao(on: boolean): void {
    this.ssaoEnabled = on;
    this.invalidate();
  }

  /**
   * Sơ đồ tách rời ("exploded view"): dời mỗi chi tiết theo vector toả tâm × `amount01` (0..1).
   * `ids` = phạm vi: null/rỗng → TOÀN model (toả quanh tâm model); else chỉ các chi tiết đó tách,
   * toả quanh tâm RIÊNG của tập chọn (local), phần còn lại giữ lắp ráp. Áp lên explodeGroup (bao ngoài
   * pivot) → không đụng chuyển động. Gọi trực tiếp khi kéo slider / đổi phạm vi cụm.
   */
  setExplode(amount01: number, ids: number[] | null = null): void {
    this.explodeAmt = Math.max(0, Math.min(1, amount01));
    this.explodeIds = ids && ids.length ? new Set(ids) : null;
    this.computeExplodeCenter();
    this.applyExplode();
    this.invalidate();
  }

  /** Mức tách rời hiện tại (0..1). */
  get explodeAmount(): number {
    return this.explodeAmt;
  }

  /**
   * Tâm toả = tâm hộp bao tâm các chi tiết TRONG phạm vi (null = toàn model), tính từ vị trí LẮP RÁP
   * (explodeCenter) nên ổn định dù đang tách dở.
   */
  private computeExplodeCenter(): void {
    let found = false;
    for (const o of this.partObjects) {
      if (this.explodeIds && !this.explodeIds.has(o.part.id)) continue;
      if (!found) {
        this._exMin.copy(o.explodeCenter);
        this._exMax.copy(o.explodeCenter);
        found = true;
      } else {
        this._exMin.min(o.explodeCenter);
        this._exMax.max(o.explodeCenter);
      }
    }
    if (found) this.explodeCenterV.addVectors(this._exMin, this._exMax).multiplyScalar(0.5);
    else this.explodeCenterV.set(0, 0, 0);
  }

  /** Áp offset tách rời: chi tiết trong phạm vi → toả; ngoài phạm vi → giữ lắp ráp (không invalidate). */
  private applyExplode(): void {
    for (const o of this.partObjects) {
      if (this.explodeIds && !this.explodeIds.has(o.part.id)) {
        o.explodeGroup.position.set(0, 0, 0);
      } else {
        explodeOffset(o.explodeCenter, this.explodeCenterV, this.explodeAmt, o.explodeGroup.position);
      }
    }
  }

  // ---------- dựng model ----------

  buildModel(doc: WvmDocument): PartObject[] {
    this.clearModel();
    this.searchHits = null;
    this.groupHl = null;
    const colorMap = buildColorMap(doc);

    // 04a nẹp: 1 material dùng CHUNG cho mỗi materialId (mọi dải cùng loại đổi màu 1 lần). Nẹp CHƯA gán
    // màu → ẨN (visible=false, trong suốt) tới khi applyBandColors() gán màu đã lưu (name→hex từ backend).
    this.bandMaterials = doc.bandMaterials ?? [];
    this.bandMaterialsById = new Map(
      this.bandMaterials.map((bm) => {
        const m = makeBandMaterial(DEFAULT_BAND_COLOR);
        m.visible = false; // chưa gán màu → trong suốt
        return [bm.id, m] as const;
      }),
    );

    // Đòn C (WVM 1.5): dựng geometry mỗi asset MỘT lần, mọi instance mesh-ref TĨNH dùng chung.
    const assetCache = new Map<string, { geo: BufferGeometry; edges: EdgesGeometry }>();
    const getAsset = (id: string): { geo: BufferGeometry; edges: EdgesGeometry } | null => {
      const raw = doc.meshAssets?.[id];
      if (!raw) return null;
      let a = assetCache.get(id);
      if (!a) {
        const geo = buildAssetGeometry(raw);
        a = { geo, edges: buildAssetEdges(geo) };
        assetCache.set(id, a);
      }
      return a;
    };

    for (const part of doc.parts) {
      const g = part.geometry;
      let mesh: Mesh | null;
      const pivot = new Group();

      if (isMeshRef(g)) {
        const asset = getAsset(g.assetId);
        if (!asset) continue; // assetId mồ côi (không có trong meshAssets) → bỏ, không vỡ
        if (part.motion) {
          // instance CÓ motion: bung về mesh-inline world-space → parity đầy đủ (pivot căn tâm như cũ).
          const inlinePart: WvmPart = { ...part, geometry: expandMeshRefToInline(g, doc.meshAssets![g.assetId]) };
          mesh = buildPartMesh(inlinePart, resolveColor(part, colorMap));
          if (!mesh) continue;
          const [cx, cy, cz] = partCenter(inlinePart);
          pivot.position.set(cx, cy, cz);
        } else {
          // instance TĨNH: dùng chung asset geo/edges; pivot mang Scale(S)∘transform (gồm cả xoay).
          // Decompose → pos/quat/scale (transform rigid + scale đều S) để matrixAutoUpdate mặc định vẫn
          // dựng lại đúng → hoà mọi hệ sẵn có (fitView/visibility/bbox) không cần đặc biệt hoá.
          mesh = buildMeshRefMesh(part, resolveColor(part, colorMap), asset.geo, asset.edges);
          meshRefPivotMatrix(g).decompose(pivot.position, pivot.quaternion, pivot.scale);
        }
      } else {
        mesh = buildPartMesh(part, resolveColor(part, colorMap), { recessGrooves: this.recessGrooves });
        if (!mesh) continue; // bỏ qua mesh-uri (chưa hỗ trợ)
        const [cx, cy, cz] = partCenter(part);
        pivot.position.set(cx, cy, cz);
      }

      pivot.add(mesh);
      // Group bao ngoài chỉ mang offset tách rời (sơ đồ lắp đặt) — không đụng pivot (MotionEngine sở hữu).
      const explodeGroup = new Group();
      explodeGroup.add(pivot);
      this.modelRoot.add(explodeGroup);

      const edge = (mesh.children.find((c) => c.userData.isEdge) as LineSegments) ?? null;
      const edgeHidden =
        (mesh.children.find((c) => c.userData.isEdgeHidden) as LineSegments) ?? null;
      const baseColor = (mesh.material as MeshPhongMaterial).color.clone();

      // nẹp: con của mesh part (mesh mang bù chuyển động) → bám cạnh khi cửa/ngăn animate.
      if (part.edgeBands?.length) this.addEdgeBands(part, mesh);
      // rãnh xoi: khoét lõm thật (04d) → thêm VIỀN miệng cho rõ nét; ngược lại vẽ decal phẳng (04c).
      let grooveRim: LineSegments | null = null;
      if (part.grooves?.length) {
        if (mesh.userData.carvedGrooves) grooveRim = this.addGrooveRims(part, mesh);
        else this.addGrooves(part, mesh);
      }
      const [ecx, ecy, ecz] = partCenter(part);
      this.partObjects.push({
        part, mesh, pivot, explodeGroup, explodeCenter: new Vector3(ecx, ecy, ecz),
        edge, edgeHidden, grooveRim, baseColor, tex: null, surface: null,
      });
    }

    // Tâm model (lúc lắp ráp) cho sơ đồ tách rời + áp lại mức đang giữ (reload giữ nguyên trạng thái).
    this.modelBounds().getCenter(this.explodeCenterV);
    this.applyExplode();

    this.applyAppearance();
    this.updateGround();
    this.fitView();
    return this.partObjects;
  }

  /**
   * 04a: dựng dải nẹp cho 1 part — gộp theo `materialId` TRONG part thành 1 mesh/loại (ít draw-call
   * nhưng vẫn bám mesh part → motion-correct). Đặt làm CON của `partMesh`; recolor qua material chung.
   */
  private addEdgeBands(part: WvmPart, partMesh: Mesh): void {
    const center = partCenter(part);
    // gộp positions/normals theo materialId; 1 dải thiếu normals → tính flat cho cả nhóm.
    const byMat = new Map<string, { pos: number[]; norm: number[] | null }>();
    for (const b of part.edgeBands ?? []) {
      if (!b.positions?.length) continue;
      let g = byMat.get(b.materialId);
      if (!g) {
        g = { pos: [], norm: [] };
        byMat.set(b.materialId, g);
      }
      const hasN = g.norm !== null && !!b.normals && b.normals.length === b.positions.length;
      g.pos.push(...b.positions);
      if (hasN) g.norm!.push(...(b.normals as number[]));
      else g.norm = null;
    }
    for (const [matId, g] of byMat) {
      const mat = this.bandMaterialsById.get(matId);
      if (!mat) continue; // materialId lạ (không có trong bandMaterials) → bỏ, không vỡ
      const bandMesh = new Mesh(buildBandGeo(g.pos, g.norm ?? undefined, center), mat);
      bandMesh.castShadow = true;
      bandMesh.receiveShadow = true;
      bandMesh.userData.isBand = true;
      partMesh.add(bandMesh);
    }
  }

  /**
   * 04c: dựng "đường khắc" rãnh xoi cho 1 part — CON của `partMesh` (bám mesh part → motion-correct).
   * v1 PHẲNG (decal): không recess/CSG; thấy ở mọi chế độ (flat/ortho/mobile) nhờ màu tối + polygonOffset.
   * Dùng 1 material chung cho mọi rãnh của part (dispose theo pivot khi đổi model).
   */
  private addGrooves(part: WvmPart, partMesh: Mesh): void {
    if (!part.grooves?.length) return;
    const center = partCenter(part);
    const mat = makeGrooveMaterial();
    for (const gr of part.grooves) {
      const geo = buildGrooveGeo(gr.path, gr.width, gr.normal, center);
      if (!geo) continue; // path/normal suy biến → bỏ rãnh đó, không vỡ
      const gMesh = new Mesh(geo, mat);
      gMesh.renderOrder = 1;
      gMesh.userData.isGroove = true;
      partMesh.add(gMesh);
    }
  }

  /**
   * 04d (A+): viền MIỆNG rãnh cho part ĐÃ khoét lõm — 1 `LineSegments` gộp mọi rãnh (con của mesh part →
   * trượt theo motion). Màu/mờ đồng bộ nét thấy trong applyAppearance. Trả null nếu không dựng được rãnh nào.
   */
  private addGrooveRims(part: WvmPart, partMesh: Mesh): LineSegments | null {
    if (!part.grooves?.length) return null;
    const center = partCenter(part);
    const pos: number[] = [];
    for (const gr of part.grooves) {
      const geo = buildGrooveRimGeo(gr.path, gr.width, gr.depth, gr.normal, center);
      if (!geo) continue;
      const a = geo.getAttribute('position');
      for (let i = 0; i < a.count; i++) pos.push(a.getX(i), a.getY(i), a.getZ(i));
      geo.dispose();
    }
    if (!pos.length) return null;
    const rimGeo = new BufferGeometry();
    rimGeo.setAttribute('position', new Float32BufferAttribute(new Float32Array(pos), 3));
    const rim = new LineSegments(rimGeo, makeGrooveRimMaterial());
    rim.renderOrder = 2; // trên fill để không bị che
    rim.userData.isGrooveRim = true;
    partMesh.add(rim);
    return rim;
  }

  /**
   * 04d: dựng LẠI mesh của 1 part grooved theo chính sách `recessGrooves` hiện tại (khoét lõm thật
   * hoặc decal). Giữ `part` gốc (geometry hộp dựng lại từ đầu = pristine), cập nhật ref edge/mesh của
   * PartObject rồi `applyAppearance()` khôi phục màu/texture/mode. Dùng cho toggle + dev-hook re-khoét.
   */
  private rebuildGroovedPart(obj: PartObject): void {
    const hex = '#' + obj.baseColor.getHexString();
    // gỡ + dispose mesh cũ (và mọi con: edge, cutout, decal rãnh)
    obj.pivot.remove(obj.mesh);
    obj.mesh.traverse((o) => {
      if (o instanceof Mesh || o instanceof LineSegments) {
        o.geometry.dispose();
        const m = o.material;
        if (Array.isArray(m)) m.forEach((x) => x.dispose());
        else m.dispose();
      }
    });
    const mesh = buildPartMesh(obj.part, hex, { recessGrooves: this.recessGrooves });
    if (!mesh) return;
    obj.pivot.add(mesh);
    obj.mesh = mesh;
    obj.edge = (mesh.children.find((c) => c.userData.isEdge) as LineSegments) ?? null;
    obj.edgeHidden = (mesh.children.find((c) => c.userData.isEdgeHidden) as LineSegments) ?? null;
    obj.grooveRim = null;
    if (obj.part.grooves?.length) {
      if (mesh.userData.carvedGrooves) obj.grooveRim = this.addGrooveRims(obj.part, mesh);
      else this.addGrooves(obj.part, mesh);
    }
    this.applyAppearance(); // khôi phục màu/opacity/map(texture) theo mode hiện tại
  }

  /**
   * 04d: đổi chế độ rãnh (lõm thật ↔ decal) lúc chạy — dựng lại mọi part CÓ rãnh. Giữ nguyên part khác.
   * Mặc định theo thiết bị (desktop lõm / mobile decal); hàm này cho user ép đổi.
   */
  setGroovesRecess(on: boolean): void {
    if (on === this.recessGrooves) return;
    this.recessGrooves = on;
    for (const o of this.partObjects) if (o.part.grooves?.length) this.rebuildGroovedPart(o);
    this.invalidate();
  }

  /** 04d: chế độ rãnh hiện tại (lõm thật = true). */
  get groovesRecess(): boolean {
    return this.recessGrooves;
  }

  /**
   * DEV: thay rãnh của 1 part lúc chạy (`window.__setGrooves`) để xem thử TRƯỚC khi plugin 08a/backend
   * 03a cấp data thật (giống `__setDims` của 04b). Gán data → dựng lại part (khoét lại / decal lại).
   */
  setPartGrooves(partId: number, grooves: Groove[]): void {
    const obj = this.partObjects.find((o) => o.part.id === partId);
    if (!obj) return;
    obj.part.grooves = grooves;
    this.rebuildGroovedPart(obj);
    this.invalidate();
  }

  /**
   * 04a: áp màu nẹp đã lưu (Viewer GET từ backend, khoá theo `name` — 03b). name→id qua
   * `bandMaterials`; loại CHƯA gán màu → ẨN (visible=false, trong suốt). Đổi material chung → mọi dải
   * cùng loại đổi/ẩn ngay.
   */
  applyBandColors(byName: Record<string, string>): void {
    if (this.bandMaterialsById.size === 0) return; // model không nẹp
    for (const bm of this.bandMaterials) {
      const mat = this.bandMaterialsById.get(bm.id);
      if (!mat) continue;
      const hex = byName[bm.name];
      if (hex != null && hex !== '') {
        mat.color.set(hex);
        mat.visible = true;
      } else {
        mat.visible = false; // chưa gán màu → trong suốt
      }
    }
    this.invalidate();
  }

  get parts(): PartObject[] {
    return this.partObjects;
  }

  private clearModel(): void {
    for (const { pivot, explodeGroup } of this.partObjects) {
      this.modelRoot.remove(explodeGroup); // pivot là con của explodeGroup → gỡ cả cụm
      pivot.traverse((o) => {
        if (o instanceof Mesh || o instanceof LineSegments) {
          o.geometry.dispose(); // nét thấy/khuất chia sẻ geometry → dispose lặp vô hại
          const m = o.material;
          if (Array.isArray(m)) m.forEach((x) => x.dispose());
          else m.dispose();
        }
      });
    }
    this.partObjects = [];
    this.textures.clear(); // dispose texture (material.dispose KHÔNG dispose .map)
  }

  /**
   * Áp texture vật liệu (04d) — gọi sau khi resolve `/materials` xong. Progressive: scene đã
   * hiện màu trơn; mỗi part GỖ có `materialId` khớp map sẽ swap sang texture khi ảnh tải xong.
   * Áp tiling-mm cho tấm `box`, panel uốn `extrusion` (UV [0,1] — planar UV 04g, faceSize=bbox shape)
   * và mesh-inline có `uvs` (UV mm từ plugin → repeat 1/step, 04f §2.2b). hardware/cylinder/mesh-uri/
   * mesh thiếu uvs → màu trơn. Lỗi tải 1 ảnh → giữ màu trơn.
   */
  applyTextures(map: Map<string, MaterialTexture>): void {
    let anyColor = false;
    for (const o of this.partObjects) {
      const mid = o.part.materialId;
      if (!mid || o.part.category === 'hardware') continue;
      const t = map.get(mid);
      if (!t) continue; // chưa map → màu trơn
      // MÀU override: thay màu WVM gốc, áp cho MỌI kind (không cần UV) → xử trước cổng geometry.
      // Độ bóng/độ trong (transparency/reflectivity/brightness) honor trong applyAppearance.
      if (t.color) {
        o.baseColor.set(t.color);
        o.tex = null;
        o.surface = { transparency: t.transparency, brightness: t.brightness, reflectivity: t.reflectivity };
        anyColor = true;
        continue;
      }
      const g = o.part.geometry;
      // kích thước mặt chính (mm) cho tiling-mm. box: cặp 2 chiều vuông góc trục MỎNG; extrusion: bbox shape (04g).
      // mesh-inline có uvs: UV đã ở mm → uvSpaceMm (repeat 1/step), không cần faceSize (04f §2.2b).
      let faceWidthMm = 0;
      let faceHeightMm = 0;
      let uvSpaceMm = false;
      if (g.kind === 'box') {
        // Mặt CHÍNH = cặp 2 chiều vuông góc trục MỎNG (bề dày). Map U/V theo BoxGeometry three:
        // thin=Z→(wx,hy) · thin=X→(dz,hy) · thin=Y→(wx,dz). Trước hard-code (wx,hy) → tấm dày dọc
        // X/Y (vd hông tủ 17×717×530) lấy nhầm 17mm làm bề rộng → texture kéo giãn phủ cả mặt (streak).
        const { wx, hy, dz } = g;
        if (dz <= wx && dz <= hy) {
          faceWidthMm = wx; // mỏng theo Z (lưng/tấm nằm) — nhánh cũ, không hồi quy
          faceHeightMm = hy;
        } else if (wx <= hy && wx <= dz) {
          faceWidthMm = dz; // mỏng theo X (hông trái/phải): mặt ±X, U dọc Z, V dọc Y
          faceHeightMm = hy;
        } else {
          faceWidthMm = wx; // mỏng theo Y (nóc/đáy đứng): mặt ±Y, U dọc X, V dọc Z
          faceHeightMm = dz;
        }
      } else if (g.kind === 'extrusion' && o.mesh.geometry.getAttribute('uv')) {
        uvSpaceMm = true; // UV extrusion đã ở mm (vách arc,z · nắp x,y) → repeat 1/step, không dùng faceSize
      } else if (isMeshInline(g) && g.uvs && o.mesh.geometry.getAttribute('uv')) {
        uvSpaceMm = true; // UV mm từ plugin; faceSize không dùng
      } else if (isMeshRef(g) && o.mesh.geometry.getAttribute('uv')) {
        uvSpaceMm = true; // Đòn C: UV mm nằm trong asset (geometry đã có attribute uv); như mesh-inline
      } else {
        continue; // cylinder / mesh-uri / mesh thiếu uvs → màu trơn
      }
      // tham số bề mặt 04e (chỉ part đã map — §1); áp trong applyAppearance ở mode 'material'.
      o.surface = {
        transparency: t.transparency,
        brightness: t.brightness,
        reflectivity: t.reflectivity,
      };
      this.textures
        .acquire({
          url: t.textureUrl,
          repeatMmU: t.repeatMmU,
          repeatMmV: t.repeatMmV,
          faceWidthMm,
          faceHeightMm,
          orientation: o.part.textureOrientation ?? 'none',
          shiftMmU: t.shiftMmU,
          shiftMmV: t.shiftMmV,
          angleDeg: t.angleDeg,
          materialOrientation: t.orientation,
          mirrored: t.mirrored,
          stretch: t.stretch,
          sharpness: t.sharpness,
          uvSpaceMm,
        })
        .then((tex) => {
          o.tex = tex;
          this.applyAppearance(); // bật map theo mode hiện tại + invalidate
        })
        .catch(() => {
          /* lỗi tải ảnh → giữ màu trơn, scene vẫn chạy */
        });
    }
    if (anyColor) this.applyAppearance(); // màu đổi đồng bộ (không async như texture) → repaint ngay
  }

  private modelBounds(): Box3 {
    // explodeGroup (bao ngoài pivot) có thể mang offset ≠ 0 → cập nhật ma trận thế giới trước khi đọc
    // bbox (expandByObject giả định parent.matrixWorld đã mới). Rẻ + chỉ gọi lúc fit/ground/thumbnail.
    this.modelRoot.updateMatrixWorld(true);
    const bb = new Box3();
    for (const { pivot } of this.partObjects) bb.expandByObject(pivot);
    if (bb.isEmpty()) bb.set(new Vector3(-10, -10, -10), new Vector3(10, 10, 10));
    return bb;
  }

  /** Khung bao model hiện tại (kể cả khi đang mở cánh) — cho đo tổng thể. */
  bounds(): Box3 {
    return this.modelBounds();
  }

  /** Đặt mặt nhận bóng tại đáy model (không còn lưới — nền gradient CSS lo phần phông). */
  private updateGround(): void {
    this.shadowPlane.position.y = this.modelBounds().min.y;
    this.updateShadowFrustum(); // model đổi → khung bao đổi → shadow camera phải bám theo
  }

  /**
   * Khớp shadow camera của đèn KEY với khung bao model.
   *
   * DirectionalLight của Three mặc định soi bóng trong hộp ortho ±5 đơn vị quanh target (gốc toạ độ).
   * Với SCALE=0.065 thì mọi model > ~154 mm đã tràn ra ngoài — mà `partCenter` giữ nguyên toạ độ BAZIS
   * nên model còn nằm lệch hẳn về một phía gốc. Phần tràn lấy mẫu NGOÀI [0,1] của shadow map, bị
   * clamp-to-edge kéo texel viền ra vô hạn → những mảng xám cạnh thẳng (hình chiếu rìa frustum) đổ lên
   * mặt kệ và mặt nhận bóng. Phải gọi lại khi model đổi (updateGround) và khi preset dựng đèn mới.
   */
  private updateShadowFrustum(): void {
    const key = this.keyLight;
    if (!key) return;
    const { center, radius } = this.modelBounds().getBoundingSphere(new Sphere());
    const r = Math.max(radius * 1.25, 1); // đệm: bóng đổ dài ra ngoài khung bao model

    this.keyTarget.position.copy(center);
    this.keyTarget.updateMatrixWorld();
    key.position.copy(center).addScaledVector(KEY_DIR, r * 3);

    const cam = key.shadow.camera;
    cam.left = -r;
    cam.right = r;
    cam.top = r;
    cam.bottom = -r;
    cam.near = r * 0.5; // đèn ở 3r → model nằm trọn trong [0.5r, 6r]
    cam.far = r * 6;
    cam.updateProjectionMatrix();
    // texel bóng giờ phủ r*2/2048 — cần bù pháp tuyến chống acne trên các tấm gỗ đồng phẳng.
    key.shadow.normalBias = r * 0.01;
    this.invalidate();
  }

  // ---------- camera ----------

  /** OrbitControls đổi (xoay/zoom/pan) → cập nhật near/far rồi vẽ lại. */
  private onControlsChange = (): void => {
    this.updateClipping();
    this.invalidate();
  };

  /**
   * Cập nhật near/far theo KHOẢNG CÁCH hiện tại (chỉ camera phối cảnh). Khi zoom vào trong,
   * dist nhỏ → near rơi về sàn nhỏ → KHÔNG còn xén chéo chi tiết trước mặt; khi ở xa, near bám
   * sát model để giữ độ chính xác z-buffer (chống z-fighting trên tấm gỗ đồng phẳng).
   */
  private updateClipping(): void {
    if (this.active !== this.camera) return; // ortho có near/far riêng
    const dist = this.camera.position.distanceTo(this.controls.target);
    const r = this.modelRadius;
    const floor = Math.max(0.05, r * 0.001);
    this.camera.near = Math.max(floor, dist - r * 2);
    this.camera.far = dist + r * 4;
    this.camera.updateProjectionMatrix();
  }

  /** Auto-fit phối cảnh (perspective) theo hướng (mặc định iso). */
  fitView(dir: Vector3 = ISO_DIR, up: Vector3 = WORLD_UP): void {
    // về chế độ phối cảnh + bật lại orbit (cho xoay)
    this.orthoDir = null;
    this.active = this.camera;
    this.controls.object = this.camera;
    this.controls.enableRotate = true;
    this.controls.enabled = true;
    this.camera.up.copy(up); // 'up' riêng cho hình chiếu bằng (nhìn thẳng xuống); mặc định +Y

    const bb = this.modelBounds();
    const sphere = bb.getBoundingSphere(new Sphere());
    const { center, radius } = sphere;
    this.modelRadius = radius;

    const fov = (this.camera.fov * Math.PI) / 180;
    const dist = (radius / Math.sin(fov / 2)) * 1.22;

    this.camera.position.copy(center).addScaledVector(dir, dist);
    this.controls.target.copy(center);
    this.controls.update();

    // đẩy model lên cao hơn trong khung để toolbar (đáy) không che mất phần dưới
    const lift = Math.tan(fov / 2) * dist * 0.12;
    const screenDown = new Vector3(0, -1, 0).applyQuaternion(this.camera.quaternion);
    this.camera.position.addScaledVector(screenDown, lift);
    this.controls.target.addScaledVector(screenDown, lift);
    this.controls.update();
    this.updateClipping(); // near/far theo khoảng cách cuối cùng
    this.invalidate();
  }

  /**
   * Preset góc nhìn — TẤT CẢ là phối cảnh (perspective), khác hướng camera.
   * Hệ trục viewer: +Z trước, +X phải, +Y trên. iso = đẳng; còn lại nhìn từ 1 phía.
   */
  viewPreset(preset: 'iso' | 'front' | 'left' | 'right' | 'top'): void {
    switch (preset) {
      case 'front':
        this.fitView(new Vector3(0, 0, 1));
        break;
      case 'left':
        this.fitView(new Vector3(-1, 0, 0));
        break;
      case 'right':
        this.fitView(new Vector3(1, 0, 0));
        break;
      case 'top':
        this.fitView(new Vector3(0, 1, 0), new Vector3(0, 0, -1)); // nhìn thẳng xuống → up = -Z
        break;
      default:
        this.fitView(); // iso
    }
  }

  /** Hình chiếu trực giao (BAZIS: +Z trước, +X phải, +Y trên). */
  private orthoView(dir: 'front' | 'side' | 'top'): void {
    this.orthoDir = dir;
    this.active = this.orthoCamera;
    this.orthoCamera.zoom = 1; // fit lại mỗi lần chọn hình chiếu

    const bb = this.modelBounds();
    const size = new Vector3();
    const center = new Vector3();
    bb.getSize(size);
    bb.getCenter(center);

    const w = this.canvas.clientWidth || 1;
    const h = this.canvas.clientHeight || 1;
    const aspect = w / h;
    const D = Math.max(size.length() * 2, 1000);
    const cam = this.orthoCamera;

    let fw: number;
    let fh: number;
    if (dir === 'front') {
      fw = size.x;
      fh = size.y;
      cam.position.set(center.x, center.y, center.z + D);
      cam.up.set(0, 1, 0);
    } else if (dir === 'side') {
      fw = size.z;
      fh = size.y;
      cam.position.set(center.x + D, center.y, center.z);
      cam.up.set(0, 1, 0);
    } else {
      fw = size.x;
      fh = size.z;
      cam.position.set(center.x, center.y + D, center.z);
      cam.up.set(0, 0, -1);
    }

    const pad = 1.2;
    fw *= pad;
    fh *= pad;
    if (fw / fh > aspect) fh = fw / aspect;
    else fw = fh * aspect;

    const off = fh * 0.08; // đẩy lên tránh toolbar đáy
    cam.left = -fw / 2;
    cam.right = fw / 2;
    cam.top = fh / 2 - off;
    cam.bottom = -fh / 2 - off;
    cam.near = 0.1;
    cam.far = D * 2 + 1000;
    cam.lookAt(center);
    cam.updateProjectionMatrix();

    // Cho phép ZOOM + PAN trên hình chiếu, nhưng KHÓA XOAY (giữ đúng trục chiếu).
    this.controls.object = this.orthoCamera;
    this.controls.enableRotate = false;
    this.controls.enabled = true;
    this.controls.target.copy(center);
    this.controls.update();
    this.invalidate();
  }

  /** Tập trung camera vào 1 part (giữ góc, chỉ dời tâm xoay). */
  focusPart(partId: number): void {
    const obj = this.partObjects.find((o) => o.part.id === partId);
    if (!obj) return;
    const [cx, cy, cz] = partCenter(obj.part);
    // + offset tách rời (nếu đang bật sơ đồ lắp đặt) để ngắm đúng vị trí HIỆN TẠI của chi tiết.
    const e = obj.explodeGroup.position;
    const target = new Vector3(cx + e.x, cy + e.y, cz + e.z);
    const delta = target.clone().sub(this.controls.target);
    this.camera.position.add(delta);
    this.controls.target.copy(target);
    this.controls.update();
    this.invalidate();
  }

  // ---------- diện mạo (selection + render mode) ----------

  setSelected(partId: number | null): void {
    this.selectedId = partId;
    this.applyAppearance();
  }

  /** Ẩn/hiện 1 part (theo pivot). Part ẩn cũng không bắt được raycast. */
  setPartVisible(partId: number, visible: boolean): void {
    const obj = this.partObjects.find((o) => o.part.id === partId);
    if (!obj) return;
    obj.pivot.visible = visible;
    this.invalidate();
  }

  /** Highlight tập chi tiết khớp tìm kiếm (mờ phần còn lại). null = tắt. */
  setSearchHighlight(ids: number[] | null): void {
    this.searchHits = ids && ids.length ? new Set(ids) : null;
    this.applyAppearance();
  }

  /**
   * Tô hổ phách các chi tiết CÙNG NHÓM chuyển động với part đang chọn (author mode) —
   * để thấy rõ "những gì sẽ mở cùng nhau". KHÔNG làm mờ phần còn lại. null = tắt.
   */
  setGroupHighlight(ids: number[] | null): void {
    this.groupHl = ids && ids.length ? new Set(ids) : null;
    this.applyAppearance();
  }

  /**
   * Tô tập chi tiết NHƯ ĐANG CHỌN (cùng màu teal + nét như click chuột) — cho "chọn cụm" ở sơ đồ
   * tách rời. Tách khỏi `setGroupHighlight` (màu cam riêng cho nhóm chuyển động Author). null = tắt.
   */
  setSelectedGroup(ids: number[] | null): void {
    this.selectedSet = ids && ids.length ? new Set(ids) : null;
    this.applyAppearance();
  }

  /** Tô nổi 1 chi tiết khi hover nhãn (sơ đồ GĐ2) — sáng hơn cụm teal. null = bỏ hover. */
  setHover(partId: number | null): void {
    if (partId === this.hoverId) return;
    this.hoverId = partId;
    this.applyAppearance();
  }

  /**
   * Điểm neo (world, scene units) của 1 chi tiết để gắn nhãn sơ đồ: tâm chi tiết + offset tách rời
   * hiện tại (explodeGroup) → luôn trỏ đúng vị trí HIỆN TẠI kể cả khi đang tách. null nếu không có id.
   */
  partAnchorWorld(partId: number): [number, number, number] | null {
    const o = this.partObjects.find((x) => x.part.id === partId);
    if (!o) return null;
    const c = o.explodeCenter;
    const e = o.explodeGroup.position;
    return [c.x + e.x, c.y + e.y, c.z + e.z];
  }

  /**
   * Zoom gọn vào tập chi tiết (giữ nguyên góc nhìn hiện tại).
   * maxWidthFraction: giới hạn bề ngang chi tiết ≤ tỉ lệ này của màn hình (vd 0.5 trên mobile
   * để không zoom quá sát). Không đặt = chỉ fit bình thường.
   */
  fitToParts(ids: number[], maxWidthFraction?: number): void {
    const objs = this.partObjects.filter((o) => ids.includes(o.part.id) && o.pivot.visible);
    if (objs.length === 0) return; // không có gì để fit → giữ nguyên view
    this.modelRoot.updateMatrixWorld(true); // explodeGroup có thể đang lệch → ma trận mới trước khi đọc bbox
    const bb = new Box3();
    for (const o of objs) bb.expandByObject(o.pivot);
    if (bb.isEmpty()) return;
    const sphere = bb.getBoundingSphere(new Sphere());
    const { center } = sphere;
    const radius = Math.max(sphere.radius, 1);

    // giữ hướng nhìn hiện tại (active camera)
    const dir = new Vector3().subVectors(this.active.position, this.controls.target);
    if (dir.lengthSq() < 1e-6) dir.copy(ISO_DIR);
    else dir.normalize();

    // luôn fit bằng perspective (rời ortho nếu đang ở hình chiếu)
    this.orthoDir = null;
    this.active = this.camera;
    this.controls.object = this.camera;
    this.controls.enableRotate = true;
    this.controls.enabled = true;

    const fov = (this.camera.fov * Math.PI) / 180;
    let dist = (radius / Math.sin(fov / 2)) * 1.3;
    // giới hạn bề ngang: bề ngang chi tiết ≤ maxWidthFraction → lùi camera đủ xa
    if (maxWidthFraction && maxWidthFraction > 0) {
      const aspect = (this.canvas.clientWidth || 1) / (this.canvas.clientHeight || 1);
      const distW = radius / (maxWidthFraction * Math.tan(fov / 2) * aspect);
      dist = Math.max(dist, distW);
    }
    this.camera.position.copy(center).addScaledVector(dir, dist);
    this.controls.target.copy(center);
    this.controls.update();

    const lift = Math.tan(fov / 2) * dist * 0.12;
    const screenDown = new Vector3(0, -1, 0).applyQuaternion(this.camera.quaternion);
    this.camera.position.addScaledVector(screenDown, lift);
    this.controls.target.addScaledVector(screenDown, lift);
    this.controls.update();
    // clipping theo bán kính TOÀN model (đã cache) để không xén phần model còn lại
    this.updateClipping();
    this.invalidate();
  }

  setRenderMode(mode: RenderMode): void {
    this.renderMode = mode;
    this.applyAppearance();
  }

  get lightingPreset(): LightingPreset {
    return this.lighting;
  }

  get lightsEnabled(): boolean {
    return this.lightsOn;
  }

  get lightIntensityLevel(): number {
    return this.lightLevel;
  }

  /** Đổi preset ánh sáng (người xem chọn). Dựng lại nhóm đèn + vẽ lại. */
  setLighting(preset: LightingPreset): void {
    if (preset === this.lighting && this.lightGroup.children.length && this.lightsOn) return;
    this.lighting = preset;
    // đang ở chế độ phẳng (tắt) → chỉ ghi nhớ preset, sẽ áp khi bật lại.
    if (this.lightsOn) this.applyLighting(preset);
    this.invalidate();
  }

  /**
   * Bật/tắt hiệu ứng chiếu sáng. **Tắt = chế độ "không hiệu ứng"** (KHÔNG tối hẳn): chỉ 1 đèn
   * môi trường trắng đầy → mẫu hiện PHẲNG (đủ màu + vân) nhưng không đổ bóng, không loé sáng
   * (specular), không gradient khối. Bật lại → khôi phục preset + cường độ đang chọn.
   */
  setLightsOn(on: boolean): void {
    this.lightsOn = on;
    if (on) this.applyLighting(this.lighting);
    else this.applyFlat();
    this.invalidate();
  }

  /** Cường độ sáng tổng theo thang 0..100 (50 = gốc preset; 100 = 2×; 0 = tối). Chỉ áp khi đang bật. */
  setLightIntensity(value: number): void {
    this.lightLevel = Math.max(0, Math.min(100, value));
    if (this.lightsOn) this.applyLightLevel();
  }

  /** Áp trọn trạng thái ánh sáng (từ payload đã lưu / khôi phục). Không đánh dấu dirty. */
  applyLightingState(s: WvmLighting): void {
    this.lighting = s.preset;
    this.lightLevel = Math.max(0, Math.min(100, s.intensity));
    this.lightsOn = s.on;
    if (s.on) this.applyLighting(s.preset);
    else this.applyFlat();
    this.invalidate();
  }

  /** Áp hệ số cường độ cho mọi đèn dựa trên cường độ gốc đã lưu (chỉ gọi khi đang bật). */
  private applyLightLevel(): void {
    const factor = this.lightLevel / 50;
    for (const c of this.lightGroup.children) {
      const base = c.userData.baseIntensity as number | undefined;
      if (typeof base === 'number') (c as unknown as { intensity: number }).intensity = base * factor;
    }
    this.invalidate();
  }

  /** Xóa + dispose mọi đèn trong nhóm (DirectionalLight.dispose giải phóng shadow map). */
  private clearLights(): void {
    this.keyLight = null;
    for (const c of [...this.lightGroup.children]) {
      this.lightGroup.remove(c);
      (c as { dispose?: () => void }).dispose?.();
    }
  }

  /** Chế độ "không hiệu ứng chiếu sáng": 1 ambient trắng → mẫu phẳng, không bóng/khối/loé.
   *  Cường độ 1.3 (>1) để màu chung sáng hơn một chút khi tắt chiếu sáng (màu ≈ color × cường độ). */
  private applyFlat(): void {
    this.clearLights();
    this.lightGroup.add(new AmbientLight(0xffffff, 1.3));
  }

  /**
   * Dựng nhóm đèn theo preset. KEY (đèn đổ bóng) chung mọi preset — chỉ đổi cường độ + đèn phụ —
   * nên bóng/hướng chính giữ nhất quán (không vỡ shadow). DirectionalLight.dispose giải phóng shadow map.
   */
  private applyLighting(preset: LightingPreset): void {
    this.clearLights();

    // KEY: hướng trên-phải, đổ bóng — luôn có; cường độ tùy preset.
    const keyIntensity = preset === 'studio' ? 1.0 : preset === 'bright' ? 0.85 : 0.7;
    const key = new DirectionalLight(0xffffff, keyIntensity);
    key.castShadow = true;
    const shadowSize = this.mobileSafe ? 1024 : 2048; // mobile-safe: ¼ bộ nhớ shadow map
    key.shadow.mapSize.set(shadowSize, shadowSize);
    key.target = this.keyTarget;
    this.lightGroup.add(key);
    this.keyLight = key;
    this.updateShadowFrustum(); // vị trí + frustum theo model (preset đổi → dựng đèn mới, phải đặt lại)

    // FILL: xanh nhạt từ phía đối, giảm tương phản vùng tối (mọi preset).
    const fill = new DirectionalLight(0xaaccff, preset === 'studio' ? 0.35 : 0.3);
    fill.position.set(-80, 50, -100);
    this.lightGroup.add(fill);

    if (preset === 'even') {
      // sáng đều mọi phía: hemisphere (trời/đất) + rim sau-trái cho mặt khuất; ambient thấp để khỏi bệt.
      this.lightGroup.add(new AmbientLight(0xffffff, 0.35));
      this.lightGroup.add(new HemisphereLight(0xffffff, 0xbfc4cc, 0.5));
      const rim = new DirectionalLight(0xffffff, 0.35);
      rim.position.set(-100, 120, -80);
      this.lightGroup.add(rim);
    } else if (preset === 'studio') {
      // 3-point: ambient thấp giữ chất khối; rim/back tách model khỏi nền.
      this.lightGroup.add(new AmbientLight(0xffffff, 0.25));
      const rim = new DirectionalLight(0xffffff, 0.5);
      rim.position.set(-40, 90, -140);
      this.lightGroup.add(rim);
    } else {
      // bright: tăng cường độ tổng (ambient cao + hemisphere) cho sáng hơn toàn cục.
      this.lightGroup.add(new AmbientLight(0xffffff, 0.7));
      this.lightGroup.add(new HemisphereLight(0xffffff, 0xcfd4dc, 0.45));
    }

    // lưu cường độ GỐC của từng đèn (để thang cường độ + on/off scale mà không cộng dồn).
    for (const c of this.lightGroup.children) {
      c.userData.baseIntensity = (c as unknown as { intensity: number }).intensity;
    }
    this.applyLightLevel();
  }

  /** Áp render mode + highlight cho mọi part (port applyVM — nhánh nền sáng). */
  private applyAppearance(): void {
    for (const o of this.partObjects) {
      const mat = o.mesh.material as MeshPhongMaterial;
      const edge = o.edge?.material as LineBasicMaterial | undefined;
      const edgeHidden = o.edgeHidden?.material as LineBasicMaterial | undefined;
      const sel = o.part.id === this.selectedId || (this.selectedSet?.has(o.part.id) ?? false);
      const hardware = o.part.category === 'hardware';

      mat.wireframe = false;
      mat.color.copy(o.baseColor);
      mat.emissive.setHex(sel ? 0x0a3a4a : 0x000000);

      // nét khuất: 'off' = ẩn; 'faint' = nhạt (tương phản nét thấy); 'bold' = đậm như nét thấy.
      let showHidden: 'off' | 'faint' | 'bold' = 'off';
      // depthOrder: ép fill(0) < nét khuất(1) < nét thấy(2) để depthTest nét thấy chặn đúng phần bị che.
      let depthOrder = false;

      switch (this.renderMode) {
        case 'material':
          mat.transparent = false;
          mat.opacity = 1;
          if (edge) {
            edge.opacity = sel ? 0.6 : 0.18;
            edge.color.setHex(sel ? SELECT_COLOR : 0x222222);
          }
          break;
        case 'matedge': // vật liệu + nét nhìn thấy ĐẬM (fill đặc tự che nét sau; KHÔNG nét khuất)
          mat.transparent = false;
          mat.opacity = 1;
          if (edge) {
            edge.opacity = sel ? 1 : 0.9;
            edge.color.setHex(sel ? SELECT_COLOR : 0x14213d);
          }
          break;
        case 'projection':
          mat.transparent = false;
          mat.opacity = 1;
          mat.color.setHex(sel ? 0xffe0cc : 0xf8f8f8);
          mat.emissive.setHex(0x000000);
          if (edge) {
            edge.opacity = sel ? 1 : 0.8;
            edge.color.setHex(sel ? SELECT_COLOR : 0x333333);
          }
          break;
        case 'xray':
          mat.transparent = true;
          mat.opacity = sel ? 0.65 : hardware ? 0.7 : 0.3;
          if (edge) {
            edge.opacity = sel ? 1 : 0.85; // nét thấy đậm
            edge.color.setHex(sel ? SELECT_COLOR : 0x123a8f);
          }
          showHidden = 'faint';
          depthOrder = true;
          break;
        case 'wireframe': // bề mặt trong suốt, KHÔNG vật liệu; MỌI nét (thấy + khuất) đều đậm
          mat.transparent = true;
          mat.opacity = 0.04;
          if (edge) {
            edge.opacity = sel ? 1 : 0.92;
            edge.color.setHex(sel ? SELECT_COLOR : 0x123a8f);
          }
          showHidden = 'bold';
          break;
        case 'wireframe-hidden': // bề mặt trong suốt; nét thấy đậm + nét khuất nhạt
          mat.transparent = true;
          mat.opacity = 0.04;
          if (edge) {
            edge.opacity = sel ? 1 : 0.92;
            edge.color.setHex(sel ? SELECT_COLOR : 0x123a8f);
          }
          showHidden = 'faint';
          depthOrder = true;
          break;
      }

      // tô hổ phách bạn-cùng-nhóm (author): glow ấm + nét cam để thấy "cụm mở cùng nhau".
      // KHÔNG áp cho chính part đang chọn (đã có tô teal) và không làm mờ phần còn lại.
      if (this.groupHl && !sel && this.groupHl.has(o.part.id)) {
        mat.emissive.setHex(0x3a2400);
        if (edge) {
          edge.opacity = Math.max(edge.opacity, 0.85);
          edge.color.setHex(0xff9500);
        }
      }

      // override khi đang tìm kiếm: chi tiết khớp nổi bật, phần còn lại mờ
      if (this.searchHits) {
        if (this.searchHits.has(o.part.id)) {
          mat.transparent = false;
          mat.opacity = 1;
          mat.emissive.setHex(0x0a3a4a);
          if (edge) {
            edge.opacity = 0.95;
            edge.color.setHex(SELECT_COLOR);
          }
        } else {
          mat.transparent = true;
          mat.opacity = 0.12;
          mat.emissive.setHex(0x000000);
          if (edge) edge.opacity = 0.12;
        }
      }

      // Nét KHUẤT: 'bold' = đậm như nét thấy (wireframe); 'faint' = nhạt rõ (wireframe-hidden / x-ray).
      if (edgeHidden) {
        if (showHidden === 'off') {
          edgeHidden.opacity = 0;
        } else {
          const dimSearch = this.searchHits != null && !this.searchHits.has(o.part.id);
          if (showHidden === 'bold') {
            edgeHidden.opacity = dimSearch ? 0.08 : sel ? 1 : 0.92;
            edgeHidden.color.setHex(sel ? SELECT_COLOR : 0x123a8f);
          } else {
            // nét nhạt — tăng độ đậm so với trước (rõ hơn) nhưng vẫn lệch hẳn nét thấy.
            edgeHidden.opacity = dimSearch ? 0.1 : sel ? 0.6 : 0.45;
            edgeHidden.color.setHex(sel ? SELECT_COLOR : 0x7e97c4);
          }
        }
      }
      // 04d: viền miệng rãnh — đồng bộ màu nét thấy, sàn độ mờ 0.6 để rãnh luôn rõ kể cả mode 'material'.
      const rim = o.grooveRim?.material as LineBasicMaterial | undefined;
      if (rim && edge) {
        rim.color.copy(edge.color);
        rim.opacity = Math.max(edge.opacity, 0.6);
      }

      // thứ tự vẽ: chỉ cần khi 'faint' (phân biệt thấy/khuất); 'bold'/'off' để mặc định.
      o.mesh.renderOrder = 0;
      if (o.edgeHidden) o.edgeHidden.renderOrder = depthOrder ? 1 : 0;
      if (o.edge) o.edge.renderOrder = depthOrder ? 2 : 0;

      // texture (04d): bật ở 'material' và 'matedge' (vật liệu + nét); mode khác tắt map.
      const useMap = this.renderMode === 'material' || this.renderMode === 'matedge' ? o.tex : null;
      if (mat.map !== useMap) mat.map = useMap;
      if (useMap) {
        mat.color.setHex(0xffffff); // map đã có màu ảnh → không nhuộm bằng baseColor
        // tham số bề mặt 04e (chỉ khi map bật ở mode 'material'). Mọi tham số 0 = trung tính.
        const s = o.surface;
        if (s) {
          // Brightness: nhân màu (linear) — giữ biến thiên vân, không bệt như emissive. cap 1.5×.
          if (s.brightness > 0) mat.color.setScalar(1 + 0.5 * (s.brightness / 100));
          // Reflectivity: modulate từ base makePhong non-metallic (30 / 0x222222). r=0 = nguyên trạng.
          mat.shininess = 30 + (s.reflectivity / 100) * 90;
          const sc = 0x22 + Math.round((0x88 - 0x22) * (s.reflectivity / 100));
          mat.specular.setHex((sc << 16) | (sc << 8) | sc); // setHex: giữ colorspace như makePhong
          // Transparency: KHÔNG ghi đè khi part đang bị search làm mờ (giữ dimming).
          const dimmedBySearch = this.searchHits != null && !this.searchHits.has(o.part.id);
          if (s.transparency > 0 && !dimmedBySearch) {
            mat.transparent = true;
            mat.opacity = 1 - s.transparency / 100;
          }
        }
      } else if (o.surface && !o.tex && (this.renderMode === 'material' || this.renderMode === 'matedge')) {
        // MÀU trơn override (không map) — honor độ bóng/độ trong trên chính màu (04e cho solid color).
        const s = o.surface;
        // Brightness: nhân màu thật (khác setScalar của texture vì đây không phải lớp phủ trắng). cap 1.5×.
        if (s.brightness > 0) mat.color.multiplyScalar(1 + 0.5 * (s.brightness / 100));
        mat.shininess = 30 + (s.reflectivity / 100) * 90;
        const sc = 0x22 + Math.round((0x88 - 0x22) * (s.reflectivity / 100));
        mat.specular.setHex((sc << 16) | (sc << 8) | sc);
        const dimmedBySearch = this.searchHits != null && !this.searchHits.has(o.part.id);
        if (s.transparency > 0 && !dimmedBySearch) {
          mat.transparent = true;
          mat.opacity = 1 - s.transparency / 100;
        }
      }

      // hover nhãn (GĐ2): tô nổi hơn cụm — emissive teal sáng + nét đậm màu chọn.
      if (this.hoverId === o.part.id) {
        mat.emissive.setHex(0x1f6b8a);
        if (edge) {
          edge.opacity = 1;
          edge.color.setHex(SELECT_COLOR);
        }
      }

      mat.needsUpdate = true;
    }
    this.invalidate();
  }

  /** Raycast theo tọa độ con trỏ (client px) → PartObject hoặc null. */
  raycast(clientX: number, clientY: number): PartObject | null {
    const rect = this.canvas.getBoundingClientRect();
    this.pointer.set(
      ((clientX - rect.left) / rect.width) * 2 - 1,
      -((clientY - rect.top) / rect.height) * 2 + 1,
    );
    this.raycaster.setFromCamera(this.pointer, this.active);
    const meshes = this.partObjects.filter((o) => o.pivot.visible).map((o) => o.mesh);
    const hits = this.raycaster.intersectObjects(meshes, false);
    if (hits.length === 0) return null;
    const id = hits[0].object.userData.partId as number;
    return this.partObjects.find((o) => o.part.id === id) ?? null;
  }

  /** Project điểm 3D → tọa độ pixel canvas (cho overlay đo đạc). */
  project(point: Vector3): { x: number; y: number } {
    const v = point.clone().project(this.active);
    const rect = this.canvas.getBoundingClientRect();
    return {
      x: ((v.x + 1) / 2) * rect.width,
      y: ((1 - v.y) / 2) * rect.height,
    };
  }

  // ---------- render on-demand ----------

  /** Yêu cầu vẽ 1 frame (gộp nhiều lời gọi trong cùng 1 rAF). */
  invalidate = (): void => {
    if (this.rafId || this.disposed) return;
    this.rafId = requestAnimationFrame(this.tick);
  };

  /** Đăng ký animator; tự xin frame tới khi animator trả false. */
  addAnimator(fn: Animator): void {
    this.animators.add(fn);
    this.invalidate();
  }

  /** Callback chạy sau mỗi lần render (vd vẽ overlay 2D dimension). */
  onAfterRender(fn: () => void): () => void {
    this.frameCallbacks.add(fn);
    return () => this.frameCallbacks.delete(fn);
  }

  private tick = (): void => {
    this.rafId = 0;
    let active = false;
    for (const a of [...this.animators]) {
      if (a()) active = true;
      else this.animators.delete(a);
    }
    this.renderFrame();
    for (const cb of this.frameCallbacks) cb();
    if (active) this.invalidate();
  };

  /** 1 lần vẽ: qua composer (SSAO) khi bật + đang perspective; ngược lại render thẳng (ortho/không AO). */
  private renderFrame(): void {
    if (this.composer && this.ssaoEnabled && this.active === this.camera) {
      this.composer.render();
    } else {
      this.renderer.render(this.scene, this.active);
    }
  }

  private handleResize(): void {
    const w = this.canvas.clientWidth || 1;
    const h = this.canvas.clientHeight || 1;
    this.renderer.setSize(w, h, false);
    this.composer?.setSize(w, h);
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
    if (this.orthoDir) this.orthoView(this.orthoDir); // refit ortho theo aspect mới
    this.invalidate();
  }

  /**
   * Chụp canvas → PNG thumbnail VUÔNG, **crop sát hộp bao model** để giảm nền dư
   * (model elongated + cửa sổ rộng trước đây cho ra ảnh model bé tí giữa nền lớn).
   * Chiếu 8 đỉnh AABB → pixel buffer, lấy khung vuông quanh tâm + lề nhỏ. Render rồi
   * drawImage trong cùng tick (không phụ thuộc preserveDrawingBuffer ở prod).
   */
  async captureThumbnail(maxW = 600): Promise<Blob> {
    this.renderer.render(this.scene, this.active); // đảm bảo buffer hiện hành
    const src = this.canvas;
    const W = src.width;
    const H = src.height;

    // mặc định: toàn canvas (khi chưa có model)
    let sx = 0,
      sy = 0,
      side = Math.min(W, H);
    sx = (W - side) / 2;
    sy = (H - side) / 2;

    if (this.partObjects.length) {
      const bb = this.modelBounds();
      const v = new Vector3();
      let minX = Infinity,
        minY = Infinity,
        maxX = -Infinity,
        maxY = -Infinity;
      for (let i = 0; i < 8; i++) {
        v.set(
          i & 1 ? bb.max.x : bb.min.x,
          i & 2 ? bb.max.y : bb.min.y,
          i & 4 ? bb.max.z : bb.min.z,
        ).project(this.active);
        const px = ((v.x + 1) / 2) * W;
        const py = ((1 - v.y) / 2) * H;
        if (px < minX) minX = px;
        if (px > maxX) maxX = px;
        if (py < minY) minY = py;
        if (py > maxY) maxY = py;
      }
      const cx = (minX + maxX) / 2;
      const cy = (minY + maxY) / 2;
      // khung vuông quanh tâm hộp + lề ~12%; kẹp trong buffer canvas (tránh viền ngoài).
      side = Math.min(Math.max(maxX - minX, maxY - minY) * 1.12, W, H);
      sx = Math.max(0, Math.min(cx - side / 2, W - side));
      sy = Math.max(0, Math.min(cy - side / 2, H - side));
    }

    const out = Math.max(1, Math.round(Math.min(maxW, side)));
    const tmp = document.createElement('canvas');
    tmp.width = out;
    tmp.height = out;
    const ctx = tmp.getContext('2d')!;
    ctx.fillStyle = '#eef4fb';
    ctx.fillRect(0, 0, out, out);
    ctx.drawImage(src, sx, sy, side, side, 0, 0, out, out);
    return new Promise<Blob>((resolve, reject) =>
      tmp.toBlob((b) => (b ? resolve(b) : reject(new Error('toBlob null'))), 'image/png'),
    );
  }

  /**
   * Scaffold chụp off-screen đúng W×H, **cùng camera hiện tại** (ADR-AR2): set pixelRatio=1 + size
   * W×H + chỉnh aspect, gọi `draw()` (caller tự mutate + render + khôi phục mutate của mình), đọc
   * canvas → PNG Blob, rồi KHÔI PHỤC size/pixelRatio/aspect trong `finally`.
   * Dùng chung cho captureHighRes (ảnh màu) và captureGuide (ảnh cấu trúc) → guide khớp pixel.
   */
  private async renderRegionToBlob(width: number, height: number, draw: () => void): Promise<Blob> {
    const W = Math.max(1, Math.round(width));
    const H = Math.max(1, Math.round(height));
    const cam = this.active;
    const prevPR = this.renderer.getPixelRatio();
    const isPersp = cam instanceof PerspectiveCamera;
    const perspAspect = isPersp ? cam.aspect : 0;
    const o = cam as OrthographicCamera;
    const orthoSnap = !isPersp ? { left: o.left, right: o.right, top: o.top, bottom: o.bottom } : null;
    try {
      this.renderer.setPixelRatio(1);
      this.renderer.setSize(W, H, false);
      const newAspect = W / H;
      if (isPersp) {
        cam.aspect = newAspect;
      } else {
        // giữ chiều dọc, co/giãn chiều ngang theo aspect mới → không méo hình
        const halfH = (o.top - o.bottom) / 2;
        const cx = (o.left + o.right) / 2;
        const halfW = halfH * newAspect;
        o.left = cx - halfW;
        o.right = cx + halfW;
      }
      cam.updateProjectionMatrix();
      draw();
      const tmp = document.createElement('canvas');
      tmp.width = W;
      tmp.height = H;
      tmp.getContext('2d')!.drawImage(this.canvas, 0, 0, W, H, 0, 0, W, H);
      return await new Promise<Blob>((resolve, reject) =>
        tmp.toBlob((b) => (b ? resolve(b) : reject(new Error('toBlob null'))), 'image/png'),
      );
    } finally {
      if (isPersp) cam.aspect = perspAspect;
      else if (orthoSnap) {
        o.left = orthoSnap.left;
        o.right = orthoSnap.right;
        o.top = orthoSnap.top;
        o.bottom = orthoSnap.bottom;
      }
      cam.updateProjectionMatrix();
      this.renderer.setPixelRatio(prevPR);
      this.renderer.setSize(this.canvas.clientWidth || 1, this.canvas.clientHeight || 1, false);
      this.invalidate();
    }
  }

  /**
   * 04a/04d: chụp khung cảnh HIỆN TẠI ở độ phân giải cao đúng **W×H** — ảnh nền cho AI Render
   * (image-to-image). KHÔNG đổi góc camera (ADR-AR2). Bỏ tô chọn/highlight để ảnh sạch.
   */
  async captureHighRes(width: number, height: number): Promise<Blob> {
    const prevSel = this.selectedId;
    const prevHits = this.searchHits;
    const prevGroupHl = this.groupHl;
    return this.renderRegionToBlob(width, height, () => {
      // ảnh sạch: bỏ tô chọn + làm mờ tìm kiếm (overlay đo là canvas 2D riêng, không lọt WebGL)
      this.selectedId = null;
      this.searchHits = null;
      this.groupHl = null;
      this.applyAppearance();
      this.renderer.render(this.scene, this.active);
      // khôi phục tô chọn (KHÔNG render lại — canvas vẫn giữ frame sạch để đọc Blob)
      this.selectedId = prevSel;
      this.searchHits = prevHits;
      this.groupHl = prevGroupHl;
      this.applyAppearance();
    });
  }

  /**
   * 04a Phase 0 — chụp PASS CẤU TRÚC (structure guide) đúng W×H, **cùng camera** ảnh màu.
   * - `'line'`      : fill ĐEN ghi depth + cạnh thấy TRẮNG → cạnh sau bị che (occlusion đúng, không X-ray).
   * - `'normal'`    : fill `MeshNormalMaterial` (mã hóa hướng bề mặt) + ẩn mọi cạnh.
   * - `'lineNormal'`: fill normal + cạnh thấy trắng đè lên.
   * Mọi mutate (material ref, visibility, clearColor) đều khôi phục; xong gọi applyAppearance() resync.
   */
  async captureGuide(
    width: number,
    height: number,
    mode: 'line' | 'normal' | 'lineNormal' = 'line',
  ): Promise<Blob> {
    const fillNormal = mode === 'normal' || mode === 'lineNormal';
    const showEdges = mode === 'line' || mode === 'lineNormal';
    const fillMat: Material = fillNormal
      ? (this.guideNormalMat ??= new MeshNormalMaterial())
      : (this.guideBlackMat ??= new MeshBasicMaterial({ color: 0x000000 }));
    const prevClear = this.renderer.getClearColor(new Color());
    const prevAlpha = this.renderer.getClearAlpha();
    const saved: { o: PartObject; mat: Material | Material[]; eVis: boolean; ehVis: boolean }[] = [];
    return this.renderRegionToBlob(width, height, () => {
      this.renderer.setClearColor(0x000000, 1); // nền đặc đen (guide KHÔNG trong suốt)
      for (const o of this.partObjects) {
        saved.push({
          o,
          mat: o.mesh.material,
          eVis: o.edge?.visible ?? false,
          ehVis: o.edgeHidden?.visible ?? false,
        });
        o.mesh.material = fillMat; // fill ghi depth → cạnh sau bị che (occlusion đúng)
        o.mesh.renderOrder = 0;
        if (o.edge) {
          o.edge.visible = showEdges;
          o.edge.renderOrder = 1;
          const e = o.edge.material as LineBasicMaterial;
          e.color.setHex(0xffffff);
          e.opacity = 1;
          e.transparent = false;
          e.depthTest = true; // bị fill (tấm trước) che
        }
        if (o.edgeHidden) o.edgeHidden.visible = false; // không vẽ cạnh khuất vào guide
      }
      this.renderer.render(this.scene, this.active);
      // khôi phục material ref + visibility; applyAppearance() resync màu/opacity/renderOrder theo mode
      for (const s of saved) {
        s.o.mesh.material = s.mat;
        if (s.o.edge) s.o.edge.visible = s.eVis;
        if (s.o.edgeHidden) s.o.edgeHidden.visible = s.ehVis;
      }
      this.renderer.setClearColor(prevClear, prevAlpha);
      this.applyAppearance();
    });
  }

  dispose(): void {
    this.disposed = true;
    if (this.rafId) cancelAnimationFrame(this.rafId);
    this.canvas.removeEventListener('webglcontextlost', this.onContextLost);
    this.canvas.removeEventListener('webglcontextrestored', this.onContextRestored);
    this.resizeObserver?.disconnect();
    this.controls.dispose();
    this.clearModel();
    this.composer?.dispose();
    this.renderer.dispose();
  }
}
